import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Info } from "lucide-react";
import { formatCount, formatYmd } from "./format";

// Avisos de honestidad de datos. NO son decorativos: cada uno explica una
// razón concreta por la que lo que se ve en pantalla no es "todo lo que pasó".
// Si se ocultan, el usuario lee los números como si fueran completos y toma
// decisiones sobre una base falsa.
//
//  1. clampedToReliableFrom → el backend acortó el rango pedido.
//  2. scatter.truncated     → el gráfico muestra una muestra de días.
//  3. timeZoneFallback      → no se pudo resolver el país; hora por defecto.
//  4. withoutTimestampApprox→ entregas sin hora, ausentes del gráfico.

/**
 * @param {{ range: object|null, delivery: object|null, scatterTruncated: boolean }} props
 */
export default function DataNotices({ range, delivery, scatterTruncated = false }) {
  const { t } = useTranslation();

  const notices = [];

  if (range?.clampedToReliableFrom) {
    notices.push({
      id: "clamped",
      level: "warn",
      text: t(
        "Acortamos el rango: solicitaste desde el {{solicitado}}, pero solo hay datos confiables desde el {{confiable}}. Lo que ves parte del {{inicio}}.",
        {
          solicitado: formatYmd(range.requestedStartDate),
          confiable: formatYmd(range.reliableFrom),
          inicio: formatYmd(range.startDate),
        }
      ),
    });
  }

  if (scatterTruncated) {
    notices.push({
      id: "truncated",
      level: "warn",
      text: t("El gráfico muestra una muestra de los días del período, no todos. Las tarjetas y el resumen sí consideran el período completo."),
    });
  }

  if (range?.timeZoneFallback) {
    notices.push({
      id: "tz-fallback",
      level: "warn",
      text: t("No pudimos determinar el país de tu operación, así que las horas se muestran en la zona horaria por defecto ({{zona}}).", {
        zona: range.timeZone || "UTC",
      }),
    });
  }

  const withoutTimestamp = Number(delivery?.withoutTimestampApprox) || 0;
  if (withoutTimestamp > 0) {
    notices.push({
      id: "no-timestamp",
      level: "info",
      text: t("{{n}} entregas no tienen hora registrada: cuentan en los totales, pero no aparecen en el gráfico de horarios.", {
        n: formatCount(withoutTimestamp),
      }),
    });
  }

  if (notices.length === 0) return null;

  return (
    <ul className="ci-notices" aria-label={t("Avisos sobre los datos")}>
      {notices.map((notice) => (
        <li key={notice.id} className={`ci-notice ci-notice--${notice.level}`}>
          <span className="ci-notice__icon" aria-hidden="true">
            {notice.level === "warn" ? <AlertTriangle size={16} /> : <Info size={16} />}
          </span>
          <span className="ci-notice__text">{notice.text}</span>
        </li>
      ))}
    </ul>
  );
}
